import type {
    AdIntelligenceRecordsSiteSummaryData,
    AdIntelligenceRecordsSummaryRecord,
    ProWebsiteServicesAdIntelligenceSiteAdvertisersSummaryData,
    ProWebsiteServicesAdIntelligenceSitePublishersSummaryData,
} from "@similarweb/pro-api-client";

import { useApiQuery } from "app/modules/api-client";
import type { Data } from "app/modules/api-client/queryTypes";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { EntitiesMetric } from "app/pages/digital-marketing/ad-intelligence/common/overview/types";
import DurationService, { BasicDurations } from "app/services/DurationService";

const DEFAULT_SUMMARY_RECORD: AdIntelligenceRecordsSummaryRecord = { count: 0, change: 0 };

type BaseEntitiesData = AdIntelligenceRecordsSiteSummaryData & {
    startDate?: string;
};

type AdvertiserEntitiesData = BaseEntitiesData & ProWebsiteServicesAdIntelligenceSitePublishersSummaryData;

type PublisherEntitiesData = BaseEntitiesData & ProWebsiteServicesAdIntelligenceSiteAdvertisersSummaryData;

export type FlattenedEntitiesData<T extends AdIntelligenceController> = T extends "advertiser"
    ? AdvertiserEntitiesData
    : PublisherEntitiesData;

export const useActivityBreakdownData = <T extends AdIntelligenceController>(controller: T) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { from, to, isWindow } = DurationService.getDurationData(params.duration).forAPI;
    const keyArray = params.key.split(",");

    const entitiesMethodName =
        controller === "advertiser" ? "adIntelligenceAdvertiserSummaryList" : "adIntelligencePublisherSummaryList";

    const breakdownMethodName =
        controller === "advertiser"
            ? "adIntelligenceAdvertiserPublishersSummaryList"
            : "adIntelligencePublisherAdvertisersSummaryList";

    const entitiesQuery = useApiQuery(
        {
            tag: "AdIntelligenceMediaPartners",
            methodName: entitiesMethodName,
        },
        {
            country: params.country,
            key: keyArray,
            from,
            to,
            isWindow,
            ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
        },
    );

    const advertisersPublishersQuery = useApiQuery(
        {
            tag: "AdIntelligenceMediaPartners",
            methodName: breakdownMethodName,
        },
        {
            country: params.country,
            key: keyArray,
            from,
            to,
            isWindow,
            ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
        },
    );

    const thirdRowEntity = controller === "advertiser" ? "publishers" : "advertisers";

    const singleDomainData = entitiesQuery.data?.data ? Object.values(entitiesQuery.data.data)[0] : undefined;

    type BreakdownApiResponse = Data<"AdIntelligenceMediaPartners", typeof breakdownMethodName>;
    type BreakdownDataValue = BreakdownApiResponse["data"][string];

    const thirdRowData = advertisersPublishersQuery.data?.data
        ? ((Object.values(advertisersPublishersQuery.data.data)[0] as BreakdownDataValue)?.[
              thirdRowEntity
          ] as EntitiesMetric)
        : undefined;

    const combinedEntitiesData: FlattenedEntitiesData<T> | undefined = singleDomainData
        ? ({
              startDate: entitiesQuery.data?.startDate,
              favicon: singleDomainData.favicon,
              campaigns: singleDomainData.campaigns ?? DEFAULT_SUMMARY_RECORD,
              creatives: singleDomainData.creatives ?? DEFAULT_SUMMARY_RECORD,
              [thirdRowEntity]: thirdRowData ?? DEFAULT_SUMMARY_RECORD,
          } as FlattenedEntitiesData<T>)
        : undefined;

    return {
        data: combinedEntitiesData,
        isLoading: entitiesQuery.isPending || advertisersPublishersQuery.isPending,
        error: entitiesQuery.isError && advertisersPublishersQuery.isError,
    };
};
